import { appstraxAuth } from '@appstrax/services/auth';
import { Operator } from '@appstrax/services/database';
import { OrganizationProjectsService, OrganizationService, OrganizationUsersService } from '../../services/organization.service';
import { ProjectService, ProjectUsersService } from '../../services/project.service';
import { Organization, OrganizationProjects, Project } from '@models';
import { normalizeUuid } from '../join-keys';
import { WorkspaceLoadPayload } from './workspace.actions';

export interface WorkspaceLoaderDeps {
  projectUsers: Pick<ProjectUsersService, 'find'>;
  orgUsers: Pick<OrganizationUsersService, 'find'>;
  orgProjects: Pick<OrganizationProjectsService, 'find'>;
  projects: Pick<ProjectService, 'find'>;
  organizations: Pick<OrganizationService, 'find'>;
  /** Injectable for tests; defaults to the SDK's authenticated user. */
  getAuthUserId?: () => Promise<string | null>;
}

function uniq(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map((v) => normalizeUuid(v)).filter((v): v is string => !!v)));
}

async function findIn<T>(
  svc: { find: (q: any) => Promise<{ data: T[] }> },
  field: string,
  ids: string[]
): Promise<T[]> {
  if (!ids.length) return [];
  const res = await svc.find({ where: { [field]: { [Operator.IN]: ids } } });
  return res.data ?? [];
}

/**
 * userId → memberships → organizations + projects + org-project links.
 *
 * Membership lookups run in parallel; entity fetches (once ids are known) also
 * run in parallel.
 */
export async function loadWorkspace(deps: WorkspaceLoaderDeps): Promise<WorkspaceLoadPayload> {
  const getAuthUserId =
    deps.getAuthUserId ?? (async () => (await appstraxAuth.getUser())?.id ?? null);
  const userId = normalizeUuid(await getAuthUserId());
  if (!userId) throw new Error('No authenticated user');

  const [projectUsersRes, orgUsersRes] = await Promise.all([
    deps.projectUsers.find({ where: { userId } }),
    deps.orgUsers.find({ where: { userId } }),
  ]);
  const projectUsers = projectUsersRes.data ?? [];
  const orgUsers = orgUsersRes.data ?? [];

  const organizationIds = uniq(orgUsers.map((x) => x.organizationId));
  const projectIds = uniq(projectUsers.map((x) => x.projectId));

  const [organizations, projects, orgProjects] = await Promise.all([
    findIn<Organization>(deps.organizations, 'id', organizationIds),
    findIn<Project>(deps.projects, 'id', projectIds),
    findIn<OrganizationProjects>(deps.orgProjects, 'organizationId', organizationIds),
  ]);

  organizations.forEach((o) => (o.id = normalizeUuid(o.id)));
  projects.forEach((p) => (p.id = normalizeUuid(p.id)));
  orgProjects.forEach((op) => {
    op.organizationId = normalizeUuid(op.organizationId);
    op.projectId = normalizeUuid(op.projectId);
  });

  return { projects, organizations, orgProjects, projectUsers, orgUsers };
}
